import { useRef, useState } from "react";
import { changeInput } from "../utils/helpers/validateFields.js";

import { FIELD_VALIDATION_RULES } from "../utils/consts/validationRule";
import { debounce } from "lodash";

import { apiFetchHouseList, apiFetchStreetList } from "../api/methods/search";

/**
 * Хук для установки значений в группу селектов и инпутов
 */
export function useAddress() {
  const [userCity, setUserCity] = useState("");
  const [userCityErrorText, setUserCityErrorText] = useState([]);

  const [userStreet, setUserStreet] = useState("");
  const [userStreetErrorText, setUserStreetErrorText] = useState([]);

  const [userHouse, setUserHouse] = useState("");
  const [userHouseErrorText, setUserHouseErrorText] = useState([]);

  const [hasFlat, setHasFlat] = useState(false);
  const [userFlat, setUserFlat] = useState("");
  const [userFlatErrorText, setUserFlatErrorText] = useState([]);

  const refCity = useRef();
  const refStreet = useRef();
  const refHouse = useRef();

  /**
   * Изменить название города
   *
   * @param {Object} value
   */
  const changeCity = value => {
    setUserFlat("");
    setUserHouse("");
    setUserStreet("");

    const options = [
      {
        type: FIELD_VALIDATION_RULES.EMPTY_LENGTH,
        targets: [value?.title],
        refs: [refCity.current?.select?.controlRef?.parentElement],
        error: "Поле не может быть пустым",
        setter: setUserCityErrorText,
      },
    ];

    changeInput(value, setUserCity, options);
  };

  /**
   * Изменить название улицы
   *
   * @param {Object} value
   */
  const changeStreet = value => {
    setUserFlat("");
    setUserHouse("");

    const options = [
      {
        type: FIELD_VALIDATION_RULES.EMPTY_LENGTH,
        targets: [value?.title],
        refs: [refStreet.current?.select?.select?.controlRef?.parentElement],
        error: "Поле не может быть пустым",
        setter: setUserStreetErrorText,
      },
    ];

    changeInput(value, setUserStreet, options);
  };

  /**
   * Изменить номер дома
   *
   * @param {Object} value
   */
  const changeHouse = value => {
    setUserFlat("");

    const options = [
      {
        type: FIELD_VALIDATION_RULES.EMPTY_LENGTH,
        targets: [value?.house],
        refs: [refHouse.current?.select?.select?.controlRef?.parentElement],
        error: "Поле не может быть пустым",
        setter: setUserHouseErrorText,
      },
    ];

    changeInput(value, setUserHouse, options);
  };

  /**
   * Изменить номер квартиры
   *
   * @param event
   */
  const changeFlat = event => {
    const value = event.target.value.replace(/^\D/, "");

    const options = [
      {
        type: FIELD_VALIDATION_RULES.EMPTY_LENGTH,
        targets: [value],
        refs: [event.target],
        error: "Поле не может быть пустым",
        setter: setUserFlatErrorText,
      },
    ];

    changeInput(value, setUserFlat, options);
  };

  const handleChangeHasFlat = () => {
    setUserFlat("");
    setHasFlat(prevState => !prevState);
  };

  /**
   * Получить список улиц
   *
   * @param {String} streetName
   * @param {String} cityName
   * @return {Promise}
   */
  const getStreetList = async (streetName, cityName) => {
    try {
      const loadedStreetList = await apiFetchStreetList(streetName, cityName);
      return loadedStreetList.filter(item =>
        item.title.toLowerCase().includes(streetName.toLowerCase())
      );
    } catch {
      return [];
    }
  };

  /**
   * Получить список домов
   *
   * @param {String} houseNumber
   * @param {Number} streetId
   * @return {Promise}
   */
  const getHouseList = async (houseNumber, streetId) => {
    try {
      const loadedHouseList = await apiFetchHouseList(streetId, parseInt(houseNumber));
      return loadedHouseList
        .filter(item => (item.house + item.frac).includes(houseNumber))
        .sort((prev, next) => {
          let prevFrac = prev.frac;
          let nextFrac = next.frac;

          if (+prev.house - +next.house === 0 && (prevFrac || nextFrac)) {
            if (prevFrac.includes("/")) prevFrac = prevFrac.slice(1);
            if (nextFrac.includes("/")) nextFrac = nextFrac.slice(1);

            return prevFrac - nextFrac || prevFrac.length - nextFrac.length;
          }

          return +prev.house - +next.house;
        });
    } catch {
      return [];
    }
  };

  const loadedStreetList = (query, callback) => {
    getStreetList(query, userCity?.alias).then(response => callback(response));
  };

  const debouncedLoadStreetList = debounce(loadedStreetList, 1000);

  const loadedHouseList = (query, callback) => {
    getHouseList(query, userStreet?.id).then(response => callback(response));
  };

  const debouncedLoadHouseList = debounce(loadedHouseList, 1000);

  return {
    userCity,
    setUserCity,
    userStreet,
    setUserStreet,
    userHouse,
    setUserHouse,
    userFlat,
    setUserFlat,
    changeCity,
    changeStreet,
    changeHouse,
    changeFlat,
    hasFlat,
    setHasFlat,
    handleChangeHasFlat,
    refCity,
    refStreet,
    refHouse,
    userCityErrorText,
    userStreetErrorText,
    userHouseErrorText,
    userFlatErrorText,
    debouncedLoadStreetList,
    debouncedLoadHouseList,
  };
}
